<template>
  <div class="py">
    <div>
      <slot name="header" />
    </div>

    <div class="max-w-4xl mx-auto px-4">
      <!-- Page header -->
      <div class="text-center mb-8">
        <h1 class="text-3xl md:text-4xl font-medium text-strongest tracking-tight">
          Select the bond offering for you
        </h1>
        <p class="text-strong text-lg font-inter">Get started with as little as $5,000</p>
      </div>

      <!-- Mobile nav tabs -->
      <div class="flex md:hidden rounded-xl bg-container-bg p-1 max-w-[350px] mx-auto mb-6">
        <button
          @click="scrollToCard('offering-s1')"
          class="flex-1 px-3 py-1 text-left bg-container-bg-dis-canvas rounded-xl h-full"
        >
          <p class="text-sm font-semibold text-strongest font-inter">9-12%</p>
          <p class="text-sm font-inter text-strong font-medium">annual interest rate</p>
        </button>
        <button @click="scrollToCard('offering-regd')" class="flex-1 px-3 py-1 text-left h-full">
          <p class="text-sm font-semibold text-strongest font-inter">9-13%</p>
          <p class="text-sm font-inter text-strong font-medium">annual interest rate</p>
        </button>
      </div>

      <!-- Offering cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
        <!-- S1 Card -->
        <div id="offering-s1" class="rounded-2xl overflow-hidden scroll-mt-20">
          <!-- Card banner -->
          <div
            class="bg-outline-dividers text-strongest whitespace-nowrap px-4 py-2.5 flex items-center gap-2 text-xs lg:text-sm font-semibold font-inter"
          >
            <span class="font-inter">Open to all investors</span>
            <span>•</span>
            <span class="font-inter">$5k minimum initial investment</span>
          </div>

          <!-- Card body -->
          <div class="bg-paper-3 dark:bg-primary-6 p-5 flex flex-col gap-5">
            <div class="flex items-center gap-2">
              <img :src="investment_gold_circle" alt="S1 icon" class="h-7 w-7" />
              <span class="text-sm font-semibold text-strongest font-inter"
                >Registered Bond Offering
              </span>
            </div>

            <div>
              <p class="text-5xl font-semibold text-strongest">9-12%</p>
              <p class="text-sm text-strongest font-inter font-semibold">Annual interest rates*</p>
            </div>

            <!-- Feature list -->
            <div class="space-y-2.5">
              <div class="flex items-start gap-2.5 text-sm text-strong font-inter">
                <div
                  class="h-6 min-w-6 bg-paper-6 dark:bg-primary-4-t flex items-center justify-center rounded-lg"
                >
                  <Icon
                    name="UserRoundPlus"
                    class="w-4 h-4 shrink-0 text-cta-tertiary-outline-icon"
                    stroke-width="3px"
                  />
                </div>
                <span class="font-inter font-medium"
                  >Open to all investors regardless of accreditation status with a lower point of
                  entry.</span
                >
              </div>
              <div
                v-for="feature in s1Features"
                :key="feature"
                class="flex items-center gap-2.5 text-sm text-strong font-inter"
              >
                <div
                  class="h-6 w-6 min-w-4 bg-paper-8 dark:bg-primary-1 flex items-center justify-center rounded-lg shrink-0"
                >
                  <Icon
                    name="Check"
                    class="w-4.5 h-4.5 text-white dark:text-black"
                    stroke-width="3px"
                  />
                </div>
                <span class="font-inter font-medium">{{ feature }}</span>
              </div>
            </div>

            <a
              href="https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&filenum=333-282862"
              target="_blank"
              class="flex items-center gap-1 text-xs font-inter text-blue-800 dark:text-blue-500"
            >
              Review offering documentation
              <Icon name="ArrowUpRight" class="w-3.5 h-3.5" />
            </a>

            <Button
              @click="handleOfferingClick('S1')"
              label
              size="md"
              variant="paper-grey-neo"
              class="w-full"
            >
              <template #label>Start investing with $5,000</template>
            </Button>
          </div>
        </div>

        <!-- Reg D Card -->
        <div id="offering-regd" class="rounded-2xl overflow-hidden scroll-mt-20">
          <!-- Card banner -->
          <div
            class="bg-outline-dividers text-strongest whitespace-nowrap px-4 py-2.5 flex items-center gap-2 text-xs lg:text-sm font-semibold font-inter"
          >
            <span class="font-inter hidden sm:inline">Accredited investors only</span>
            <span class="font-inter sm:hidden">Accredited Only</span>
            <span>•</span>
            <span class="font-inter">$25k minimum initial investment</span>
          </div>

          <!-- Card body -->
          <div class="bg-paper-5 dark:bg-primary-4-t p-5 flex flex-col gap-5">
            <div class="flex items-center gap-2">
              <img :src="investment_gold_star" alt="Reg D icon" class="h-7 w-7" />
              <span class="text-sm font-semibold text-strongest font-inter"
                >Private Placement Bond Offering</span
              >
            </div>

            <div>
              <p class="text-5xl font-semibold text-strongest">9-13%</p>
              <p class="text-sm text-strongest font-inter font-semibold">Annual interest rates*</p>
            </div>

            <!-- Feature list -->
            <div class="space-y-2.5">
              <div class="flex items-start gap-2.5 text-sm text-strong font-inter">
                <div
                  class="h-6 min-w-6 bg-paper-6 dark:bg-primary-5 flex items-center justify-center rounded-lg"
                >
                  <Icon
                    name="UserRoundPlus"
                    class="w-4 h-4 shrink-0 text-cta-tertiary-outline-icon"
                    stroke-width="3px"
                  />
                </div>
                <span class="font-inter font-medium"
                  >Open to accredited investors seeking to diversify their portfolio with higher
                  interest rates.</span
                >
              </div>
              <div
                v-for="feature in regDFeatures"
                :key="feature"
                class="flex items-center gap-2.5 text-sm text-strong font-inter"
              >
                <div
                  class="h-6 w-6 min-w-4 bg-primary-8 dark:bg-secondary-6 flex items-center justify-center rounded-lg shrink-0"
                >
                  <Icon
                    name="Check"
                    class="w-4.5 h-4.5 text-white dark:text-black"
                    stroke-width="3px"
                  />
                </div>
                <span class="font-inter font-medium">{{ feature }}</span>
              </div>
            </div>

            <a
              href="https://phoenixenergy.com/ppm/?_gl=1*93svuu*_gcl_au*MTg4NTkxMjM0OS4xNzcxNTQ2OTI4"
              target="_blank"
              class="flex items-center gap-1 text-xs font-inter text-blue-800 dark:text-blue-500 hover:underline w-fit"
            >
              Review private placement memorandum
              <Icon name="ArrowUpRight" class="w-3.5 h-3.5" />
            </a>

            <Button
              @click="handleOfferingClick('Reg D')"
              label
              size="md"
              variant="primary-secondary-neo"
              class="w-full"
            >
              <template #label>Start investing with $25,000</template>
            </Button>
          </div>
        </div>
      </div>

      <!-- Help section -->
      <div class="text-center mt-[57px] mb-4">
        <h2 class="text-2xl md:text-4xl font-medium text-strongest tracking-[-0.72px]">
          Have questions about investing?
        </h2>
        <p class="text-strong font-inter mt-1">Reach out to us!</p>

        <div class="flex flex-col items-center gap-3 mt-6">
          <Button
            @click="openCalendly"
            label
            size="md"
            variant="primary-grey"
            class="w-full md:w-auto"
          >
            <template #icon
              ><Icon name="Handshake" class="w-5 h-5 text-cta-secondary-outline-icon"
            /></template>
            <template #label>
              <span class="hidden lg:inline font-inter"
                >Meet with a Licensed Investment Professional</span
              >
              <span class="lg:hidden font-inter">Meet a Licensed Investment Pro</span>
            </template>
          </Button>

          <Button @click="callUs" label size="md" variant="paper-primary" class="w-full md:w-auto">
            <template #label>Call us at 855-732-6395</template>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { Button, Icon } from "@/components";
import { useDataStore, usePurchaseFlowStore, useCrudStore } from "@/stores";
import { useAnalytics } from "@/composables/useAnalytics";

import investment_gold_circle from "@/assets/images/investment_gold_circle.png";
import investment_gold_star from "@/assets/images/investment_gold_star.png";

defineProps({
  parentScreen: {
    type: String,
    required: false,
  },
});

const router = useRouter();
const dataStore = useDataStore();
const crudStore = useCrudStore();
const purchaseFlowStore = usePurchaseFlowStore();
const { trackOfferingCtaClicked } = useAnalytics();

function scrollToCard(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 80;
  window.scrollTo({ top, behavior: "smooth" });
}

const s1Features = [
  "$5k minimum initial investment",
  "Open to all investors",
  "3, 5, 7, & 11-year terms",
  "IRA eligible",
  "Monthly interest payments or monthly compounding interest",
];

const regDFeatures = [
  "$25k minimum initial investment",
  "Open to accredited investors only",
  "1, 3, 5, 7, & 11-year terms",
  "IRA eligible",
  "Monthly interest payments or monthly compounding interest",
];

const handleOfferingClick = async (type) => {
  const isRegD = type === "Reg D";
  trackOfferingCtaClicked(
    isRegD ? "Start investing with $25,000" : "Start investing with $5,000",
    isRegD ? 25000 : 5000,
    isRegD ? "Reg D" : "S1"
  );

  const matchingOfferings = dataStore.offerings.filter((off) =>
    isRegD
      ? off.type === "Reg D" && off.tier === purchaseFlowStore.regDTier && !off.is_short_term
      : off.type === "S1" && !off.is_short_term
  );

  const selectedOffering = matchingOfferings.sort((a, b) => b.interest_rate - a.interest_rate)[0];
  purchaseFlowStore.selected_offering = selectedOffering;

  purchaseFlowStore.current_investment.is_short_term = false;
  purchaseFlowStore.current_investment.is_compounded = false;

  const defaultAmount = isRegD ? 25000 : selectedOffering.is_short_term ? 1000 : 5000;
  const currentAmount = purchaseFlowStore.current_investment.amount || 0;
  const previousType = purchaseFlowStore.current_investment.offering_type;

  // Reinvestors arrive here with the amount already set to what's available to roll
  // over — never stomp it with the offering's default starting amount.
  if (
    !purchaseFlowStore.selected_rollover &&
    (previousType !== type || currentAmount <= defaultAmount)
  ) {
    purchaseFlowStore.current_investment.amount = defaultAmount;
  }

  purchaseFlowStore.current_step = 1;
  router.push("/make-an-investment");
  await purchaseFlowStore.saveInvestmentProgress();
};

function openCalendly() {
  let log = {
    step: "Experiment CTA",
    substep: "Meet with a Licensed Investment Professional",
    status: "Navigated",
    message: "",
    url: "https://calendly.com/d/cxfk-wk9-57b/connect-with-phoenix-energy",
    validation_details: null,
  };
  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
  window.open(
    "https://calendly.com/d/cxfk-wk9-57b/connect-with-phoenix-energy?utm_source=FP&utm_medium=portal&utm_campaign=support",
    "_blank"
  );
}

function callUs() {
  let log = {
    step: "Experiment CTA",
    substep: "Call Us",
    status: "Navigated",
    message: "",
    url: "tel:8557326395",
    validation_details: null,
  };
  log = dataStore.setDefaultFields(log, "investoractivity");
  crudStore.insert("investoractivity", [log]);
  window.location.href = "tel:8557326395";
}
</script>
